/**
 * Loans › Tools › Test approval card — LOP-owned (Bill 2026-11-03, acceptance test of the Loans Chat Bot on the Agents SDK).
 *
 *   POST v3/loans/admin/tools/test-card              (tools.test-card, reason required)  → { askId, sentTo, delivered, failed, message }
 *   GET  v3/loans/admin/tools/test-card              (tools.test-card.list)              → { sentTo, maxOpen, items: [...] }
 *   POST v3/loans/admin/tools/test-card/{id}/cancel  (tools.test-card.cancel, reason required)
 *
 * The API creates a real test ask (kind 'bot-test') and the Loans Chat Bot sends its card to the signed-in person's EVERYDAY Teams
 * account (the admin account has no Teams). Approve / Reject in Teams settles it through the normal in-card path; nothing outside
 * the test is written and nobody else is told. While a card is open this page re-reads every 5 seconds to show the press.
 */

import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@retail-ops/panel-shell';
import { Banner, Button, Card, Pill, Spinner, type PillTone } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { loansApi } from '../loansApi';
import { modalError } from '../adminKit';
import { errorText, fmtCt } from '../writeups/common';

const ROUTE = 'v3/loans/admin/tools/test-card';
const POLL_MS = 5000;

interface TestCard {
  askId: number;
  token: string;
  status: string;
  /** Open | Approved | Rejected | Cancelled | Expired */
  outcome: string;
  settledAt: string | null;
  settledBy: string | null;
  /** 'bot' = pressed in Teams; 'app' = answered in an app. */
  channel: string | null;
  comment: string | null;
  sentTo: string | null;
  requestedAt: string;
  delivered: number;
  failed: number;
  cardFailure: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  expired: boolean;
}

interface ListAnswer {
  sentTo: string | null;
  problem: string | null;
  maxOpen: number;
  items: TestCard[];
}

interface SendAnswer {
  askId: number | null;
  sentTo: string | null;
  delivered: number;
  failed: number;
  code: string | null;
  message: string;
}

const outcomeTone: Record<string, PillTone> = {
  Open: 'info',
  Approved: 'ok',
  Rejected: 'err',
  Cancelled: 'muted',
  Expired: 'muted',
};

function via(c: TestCard): string {
  if (c.outcome === 'Open') return c.delivered > 0 ? 'Waiting for your press in Teams' : 'The card did not reach Teams';
  if (c.status === 'Settled') return c.channel === 'bot' ? 'Pressed in Teams' : c.channel ? `Answered in the ${c.channel}` : 'Answered';
  if (c.outcome === 'Expired') return 'Not answered within 24 hours';
  return c.cancelReason ? `Cancelled: ${c.cancelReason}` : 'Cancelled';
}

export function LoansTestCardPage(): JSX.Element {
  const { status } = usePlatforms();
  const canCancel = status.loans.kind === 'ready' && status.loans.caps.actions.some((a) => a.id === 'tools.test-card.cancel');

  const [data, setData] = useState<ListAnswer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [result, setResult] = useState<SendAnswer | null>(null);

  // One dialog for both writes: send a new card, or cancel an open one.
  const [dialog, setDialog] = useState<{ kind: 'send' } | { kind: 'cancel'; card: TestCard } | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await loansApi.get<ListAnswer>(ROUTE));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anyOpen = (data?.items ?? []).some((c) => c.outcome === 'Open');
  useEffect(() => {
    if (!anyOpen) return undefined;
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(t);
  }, [anyOpen, load]);

  const openDialog = (d: NonNullable<typeof dialog>): void => {
    setReason(d.kind === 'send' ? 'Acceptance test of the Loans Chat Bot' : '');
    setTried(false);
    setSaveError(null);
    setDialog(d);
  };
  const closeDialog = (): void => {
    if (!saving) setDialog(null);
  };

  const submit = async (): Promise<void> => {
    if (!dialog) return;
    setTried(true);
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (dialog.kind === 'send') {
        const r = await loansApi.send<SendAnswer>('POST', ROUTE, {}, asciiReason(reason));
        setResult(r);
      } else {
        await loansApi.send('POST', `${ROUTE}/${dialog.card.askId}/cancel`, {}, asciiReason(reason));
        setResult(null);
      }
      setDialog(null);
      await load();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const items = data?.items ?? [];
  const openCount = items.filter((c) => c.outcome === 'Open').length;
  const atLimit = data ? openCount >= data.maxOpen : false;

  const columns: ReadonlyArray<DataTableColumn<TestCard>> = [
    {
      header: 'Test card',
      cell: (c) => (
        <>
          <span className="cell-strong">{c.token}</span>
          <div className="cell-desc">Sent {fmtCt(c.requestedAt)}</div>
        </>
      ),
      sortKey: (c) => c.askId,
      cardTitle: true,
    },
    { header: 'Outcome', cell: (c) => <Pill tone={outcomeTone[c.outcome] ?? 'muted'}>{c.outcome}</Pill>, sortKey: (c) => c.outcome },
    {
      header: 'When',
      cell: (c) => (c.settledAt ? fmtCt(c.settledAt) : c.cancelledAt ? fmtCt(c.cancelledAt) : '—'),
      sortKey: (c) => c.settledAt ?? c.cancelledAt ?? '',
    },
    {
      header: 'How',
      cell: (c) => (
        <>
          {via(c)}
          {c.comment && <div className="cell-desc">&ldquo;{c.comment}&rdquo;</div>}
          {c.outcome === 'Open' && c.failed > 0 && c.cardFailure && <div className="cell-desc">{c.cardFailure}</div>}
        </>
      ),
    },
    { header: 'Sent to', cell: (c) => <span className="cell-desc">{c.sentTo ?? '—'}</span> },
    {
      header: '',
      cell: (c) =>
        c.outcome === 'Open' && canCancel ? (
          <Button variant="ghost" size="sm" onClick={() => openDialog({ kind: 'cancel', card: c })}>
            Cancel
          </Button>
        ) : (
          <></>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Test approval card"
        intro={
          <>
            Sends a real approval card from the Loans Chat Bot to your own everyday Teams account
            {data?.sentTo ? ` (${data.sentTo})` : ''}, with Approve and Reject buttons. Press one in Teams: the card is replaced by the
            decided card, exactly as a real approval does — but nothing real is approved, nothing is written to any write-up, and
            nobody else is told. Use it to check the bot end to end. At most {data?.maxOpen ?? 3} can be open at a time; an unanswered
            one expires after 24 hours.
          </>
        }
        actions={
          <Button onClick={() => openDialog({ kind: 'send' })} disabled={!data || !data.sentTo || atLimit}>
            Send me a test approval card
          </Button>
        }
      />

      {data?.problem && <Banner tone="warn">{data.problem}</Banner>}
      {atLimit && (
        <Banner tone="info">
          You have {openCount} open test cards. Answer one in Teams{canCancel ? ' or cancel one below' : ''} before sending another.
        </Banner>
      )}
      {result && (
        <Banner tone={result.delivered > 0 ? 'info' : 'warn'}>
          {result.message} <Button variant="ghost" size="sm" onClick={() => setResult(null)}>Dismiss</Button>
        </Banner>
      )}
      {loadError && (
        <Banner tone="err">
          Could not load your test cards: {loadError} <Button variant="ghost" size="sm" onClick={() => void load()}>Retry</Button>
        </Banner>
      )}

      {!data && !loadError ? (
        <Spinner label="Loading your test cards…" />
      ) : (
        <Card title={anyOpen ? 'Your recent test cards — checking for your press every few seconds' : 'Your recent test cards'}>
          <DataTable<TestCard>
            columns={columns}
            rows={items}
            rowKey={(c) => String(c.askId)}
            emptyState={<p className="empty">You have not sent a test card yet.</p>}
          />
        </Card>
      )}

      <ModalShell
        open={dialog !== null}
        title={dialog?.kind === 'cancel' ? `Cancel ${dialog.card.token}?` : 'Send me a test approval card'}
        onClose={closeDialog}
        width={560}
        actions={
          <>
            <Button variant="ghost" onClick={closeDialog} disabled={saving}>
              Back
            </Button>
            <Button onClick={() => void submit()} disabled={saving}>
              {saving ? 'Working…' : dialog?.kind === 'cancel' ? 'Cancel the test card' : 'Send the card'}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          {saveError && <Banner tone="err">{saveError}</Banner>}
          <p>
            {dialog?.kind === 'cancel'
              ? 'The card in Teams changes to say it was cancelled. Nothing else happens.'
              : `The Loans Chat Bot sends the card to ${data?.sentTo ?? 'your everyday Teams account'}. It says it is a test, shows your reason, and has Approve and Reject buttons (Reject asks for a reason).`}
          </p>
          <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
        </div>
      </ModalShell>
    </div>
  );
}
